import { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import Navbar from "@/pages/home/components/Navbar";
import CTAFooterSection from "@/pages/home/components/CTAFooterSection";

function buildSiteBaseUrl(): string {
  const basePath = __BASE_PATH__.split("/").filter(Boolean).join("/");
  const pathPrefix = basePath ? `/${basePath}` : "";
  return `${window.location.origin}${pathPrefix}`;
}

export default function ModerateBanner() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const acao = searchParams.get("acao");

  const [loading, setLoading] = useState(true);
  const [approved, setApproved] = useState(false);
  const [rejected, setRejected] = useState(false);
  const [error, setError] = useState("");
  const [bannerTitle, setBannerTitle] = useState("");

  useEffect(() => {
    let cancelled = false;

    async function handleModeration() {
      if (!id || !token) {
        setError("Link inválido ou incompleto.");
        setLoading(false);
        return;
      }
      if (acao !== "aprovar" && acao !== "recusar") {
        setError("Ação inválida neste link.");
        setLoading(false);
        return;
      }

      try {
        // 1. Valida token no servidor e carrega o anúncio pra exibição (sem expor approval_token).
        const { data: loadData } = await supabase.rpc("moderation_load", {
          p_banner_id: Number(id),
          p_token: token,
        });

        if (!loadData || loadData.status === "nao_encontrado") {
          setError("Anúncio não encontrado.");
          setLoading(false);
          return;
        }

        if (loadData.status === "token_invalido") {
          setError(
            loadData.ja_revisado
              ? "Este anúncio já foi revisado anteriormente."
              : "Este link de moderação é inválido ou já foi utilizado.",
          );
          setLoading(false);
          return;
        }

        const banner = loadData.banner;
        setBannerTitle(banner?.title || "o anúncio");

        const isApprove = acao === "aprovar";

        // 2. Aprova/recusa no servidor e já recebe nome/email da integrante.
        const { data: modData } = await supabase.rpc("moderate_banner", {
          p_banner_id: Number(id),
          p_token: token,
          p_aprovar: isApprove,
        });

        if (!modData || modData.status === "token_invalido") {
          setError("Não foi possível processar a moderação. Tente novamente.");
          setLoading(false);
          return;
        }

        // 3. Notifica a membro por email (aprovado ou recusado).
        if (modData.member_email) {
          supabase.functions
            .invoke(
              isApprove ? "notify-banner-approved" : "notify-banner-rejected",
              {
                body: {
                  memberName: modData.member_name || "Integrante",
                  memberEmail: modData.member_email,
                  bannerTitle: modData.title || "seu anúncio",
                  baseUrl: buildSiteBaseUrl(),
                },
              },
            )
            .then(({ error: fnError }) => {
              if (fnError) console.error("Falha ao notificar membro:", fnError);
            });
        }

        if (isApprove) setApproved(true);
        else setRejected(true);
      } catch (err) {
        console.error("Erro ao processar moderação:", err);
        setError("Erro ao processar sua solicitação. Tente novamente.");
      }

      if (!cancelled) setLoading(false);
    }

    handleModeration();

    return () => {
      cancelled = true;
    };
  }, [id, token, acao]);

  if (loading) {
    return (
      <div className="min-h-screen bg-beige flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-primary/20 border-t-primary rounded-full animate-spin mx-auto mb-4" />
          <p className="font-body text-sm text-brown/60">Processando a moderação...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <section className="pt-32 pb-20 md:pt-40 md:pb-28 bg-beige">
        <div className="w-full px-6 md:px-10 lg:px-16 max-w-lg mx-auto">
          <div className="bg-white rounded-2xl p-8 md:p-10 text-center">
            {error ? (
              <>
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-red-100 flex items-center justify-center">
                  <i className="ri-error-warning-line text-2xl text-red-500" />
                </div>
                <h2 className="font-display text-xl text-brown mb-3">Ops!</h2>
                <p className="font-body text-sm text-brown/70 leading-relaxed mb-6">
                  {error}
                </p>
                <a
                  href="/admin"
                  className="inline-block px-6 py-3 rounded-full bg-primary text-white font-body text-sm tracking-wide uppercase hover:bg-primary-dark transition-colors whitespace-nowrap"
                >
                  Ir para o painel admin
                </a>
              </>
            ) : approved ? (
              <>
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-green-100 flex items-center justify-center">
                  <i className="ri-check-line text-2xl text-green-600" />
                </div>
                <h2 className="font-display text-xl md:text-2xl text-brown mb-3">
                  Anúncio publicado!
                </h2>
                <p className="font-body text-sm text-brown/70 leading-relaxed mb-6">
                  O anúncio <strong>{bannerTitle}</strong> foi aprovado e já está
                  no ar no carrossel da página inicial. A integrante foi avisada
                  por email.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <a
                    href="/admin"
                    className="inline-block px-6 py-3 rounded-full bg-primary text-white font-body text-sm tracking-wide uppercase hover:bg-primary-dark transition-colors whitespace-nowrap"
                  >
                    Voltar ao painel admin
                  </a>
                  <a
                    href="/"
                    className="inline-block px-6 py-3 rounded-full bg-beige border border-nude text-brown font-body text-sm tracking-wide uppercase hover:bg-nude/50 transition-colors whitespace-nowrap"
                  >
                    Ver o site
                  </a>
                </div>
              </>
            ) : rejected ? (
              <>
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-amber-100 flex items-center justify-center">
                  <i className="ri-close-line text-2xl text-amber-600" />
                </div>
                <h2 className="font-display text-xl md:text-2xl text-brown mb-3">
                  Anúncio recusado
                </h2>
                <p className="font-body text-sm text-brown/70 leading-relaxed mb-6">
                  O anúncio <strong>{bannerTitle}</strong> foi recusado e não será
                  exibido no site. A integrante foi avisada por email de forma
                  acolhedora.
                </p>
                <a
                  href="/admin"
                  className="inline-block px-6 py-3 rounded-full bg-primary text-white font-body text-sm tracking-wide uppercase hover:bg-primary-dark transition-colors whitespace-nowrap"
                >
                  Voltar ao painel admin
                </a>
              </>
            ) : null}
          </div>
        </div>
      </section>

      <CTAFooterSection />
    </div>
  );
}